import { Box, FormHelperText, Grid } from '@mui/material';
import { Field, FieldProps, Formik, FormikProps, isNaN, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';
import { PaloFormSection } from '@synergy/components-palo';
import {
    PoolFormattedMessage,
    PoolMarkdownEditor,
    PoolSpinner,
    PoolTextField,
    ScrollToFieldError,
} from '@synergy/components';
import { ReactElement } from 'react';
import { SHOP_URL } from '@synergy/config';
import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import urlJoin from 'url-join';

import * as StaticSitesActions from '../store/static-sites-actions';
import * as StaticSitesDetailActions from './store/static-sites-detail-actions';
import { PaloContentDetailShell } from '../../palo/palo-content-detail-shell';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { SiteLink } from '../../components/site-link';
import { StaticSitesFormValues } from './models/static-sites-form-values';
import { getValidationSchema } from './static-sites-detail-validation-schema';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import {
    staticSitesDetailFormValues,
    staticSitesDetailNonUniqueErrorSelector,
    staticSitesDetailRequestingSelector,
    staticSitesDetailSelector,
} from './store/static-sites-detail-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export const StaticSitesDetail = () => {
    const requesting = useAppSelector(staticSitesDetailRequestingSelector);
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const staticSitesFormValues = useAppSelector(staticSitesDetailFormValues);
    const staticSites = useAppSelector(staticSitesDetailSelector);
    const hasNonUniqueProperties = useAppSelector(staticSitesDetailNonUniqueErrorSelector);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const dispatch = useThunkDispatch();
    const { id: staticSitesIdParam } = useParams<{ id: string }>();
    const intl = useIntl();
    const navigate = useNavigate();
    const formikRef = useRef<FormikProps<StaticSitesFormValues>>(null);

    useEffect(() => {
        if (staticSitesIdParam && !isNaN(+staticSitesIdParam)) {
            dispatch(StaticSitesDetailActions.fetchStaticSitesDetail(+staticSitesIdParam));
        }

        return () => {
            dispatch(StaticSitesDetailActions.resetStaticSitesDetail());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isNaN(Number(staticSitesIdParam)) && staticSites?.id) {
            navigate(ROUTES.contentManagement.staticSites.detail.replace(':id', staticSites.id.toString()));
        }
        // ignore staticSitesIdParam, should only rerun if staticSites.id changes when new staticSite is created
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate, staticSites?.id]);

    useEffect(() => {
        if (hasNonUniqueProperties) {
            formikRef.current?.setFieldError('path', 'app.error.nonUniquePath');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasNonUniqueProperties]);

    const handleSubmit = (values: StaticSitesFormValues) => {
        dispatch(StaticSitesDetailActions.saveStaticSitesDetail(values));
    };

    const handleDelete = () => {
        if (staticSitesIdParam && !isNaN(staticSitesIdParam)) {
            dispatch(StaticSitesActions.deleteStaticSites(+staticSitesIdParam));
        }
    };

    const isNew = isNaN(Number(staticSitesIdParam));

    return (
        <PoolSpinner enabled={requesting} sx={{ width: '100%', height: '100%' }}>
            <Formik
                innerRef={formikRef}
                initialValues={staticSitesFormValues}
                onSubmit={handleSubmit}
                validationSchema={getValidationSchema(selectedLanguage)}
                enableReinitialize={true}
            >
                <PaloContentDetailShell
                    title="app.contentManagement.staticSites.detailHeader"
                    isNew={isNew}
                    onDelete={handleDelete}
                    listRoute={ROUTES.contentManagement.staticSites.list}
                    deleteTitle={<FormattedMessage id="app.contentManagement.staticSites.delete" />}
                    deleteWarning={<StaticSitesDeleteWarning />}
                >
                    <ScrollToFieldError />

                    <Box m={theme => theme.spacing(2, theme.general.pageContentSpacing)}>
                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.staticSites.text" />}>
                            <Grid container={true} spacing={2}>
                                <Grid item={true} xs={12}>
                                    <Field name={`title.${selectedLanguage}`}>
                                        {({ field, form, meta }: FieldProps<string>) => (
                                            <PoolTextField
                                                {...field}
                                                fullWidth={true}
                                                label={
                                                    <FormattedMessage id="app.contentManagement.staticSites.title" />
                                                }
                                                required={true}
                                                value={field.value}
                                                onChange={e => form.setFieldValue(field.name, e.target.value)}
                                                error={!!meta.touched && !!meta.error}
                                                helperText={
                                                    meta.touched && meta.error ? (
                                                        <FormattedMessage id={meta.error} />
                                                    ) : (
                                                        <FormattedMessage
                                                            id="app.contentManagement.staticSites.titleCharacterCount"
                                                            values={{ value: field.value?.length ?? 0 }}
                                                        />
                                                    )
                                                }
                                            />
                                        )}
                                    </Field>
                                </Grid>
                                <Grid item={true} xs={12}>
                                    <Field name={`text.${selectedLanguage}`}>
                                        {({ field, form, meta }: FieldProps<string>) => (
                                            <>
                                                {/* maxHeight for MarkdownEditor to be resizeable from min to maxHeight */}
                                                <Box maxHeight={346}>
                                                    <PoolMarkdownEditor
                                                        {...field}
                                                        value={field.value}
                                                        onChange={value => form.setFieldValue(field.name, value)}
                                                        error={!!meta.touched && !!meta.error}
                                                        minHeight={300}
                                                    />
                                                </Box>
                                                {meta.touched && meta.error ? (
                                                    <FormHelperText error={true} sx={{ ml: 1.75 }}>
                                                        <FormattedMessage id={meta.error} />
                                                    </FormHelperText>
                                                ) : null}
                                            </>
                                        )}
                                    </Field>
                                </Grid>
                            </Grid>
                        </PaloFormSection>

                        <PaloFormSection title={<FormattedMessage id="app.contentManagement.staticSites.settings" />}>
                            <Grid container={true} spacing={2}>
                                <Field name="path">
                                    {({ field, meta }: FieldProps<string>) => {
                                        const url = urlJoin(SHOP_URL, field.value);
                                        return (
                                            <>
                                                <Grid item={true} xs={12}>
                                                    <PoolTextField
                                                        {...field}
                                                        fullWidth={true}
                                                        value={field.value}
                                                        label={
                                                            <FormattedMessage id="app.contentManagement.staticSites.pathLabel" />
                                                        }
                                                        required={true}
                                                        placeholder={intl.formatMessage({
                                                            id: 'app.contentManagement.staticSites.pathPlaceholder',
                                                        })}
                                                        disabled={selectedLanguage !== currentLocale}
                                                        error={!!meta.touched && !!meta.error}
                                                        helperText={
                                                            meta.touched &&
                                                            meta.error && <FormattedMessage id={meta.error} />
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item={true} xs={12}>
                                                    <PoolFormattedMessage id="app.contentManagement.staticSites.link" />
                                                    &nbsp;
                                                    <SiteLink href={url} target="_blank">
                                                        {url}
                                                    </SiteLink>
                                                </Grid>
                                            </>
                                        );
                                    }}
                                </Field>
                            </Grid>
                        </PaloFormSection>
                    </Box>
                </PaloContentDetailShell>
            </Formik>
        </PoolSpinner>
    );
};

/** The warning names the entry, which only the form context knows. */
const StaticSitesDeleteWarning = (): ReactElement => {
    const currentLocale = useAppSelector(state => state.currentLocale);
    const { values } = useFormikContext<StaticSitesFormValues>();

    return (
        <PoolFormattedMessage
            id="app.contentManagement.staticSites.deleteWarning"
            values={{ name: values.title[currentLocale] }}
        />
    );
};
